import { useState, useCallback, useMemo } from 'react';
import { useFileDialog } from '@/hooks/useFileDialog';
import CloudSourceHint from '@/components/CloudSourceHint';
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  Layers,
  Minimize2,
  FileText,
  ImageIcon,
  Lock,
  Scissors,
  CheckCircle,
  XCircle,
  Loader2,
  Download,
  Save,
  Archive,
  X,
  GripVertical,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';
import { pdfjsLib } from '@/lib/pdfWorker';
import { ensurePdfLibReadable, PdfLockedError } from '@/lib/pdfDecrypt';
import { pdfLockedFilesMessage } from '@/lib/pdfIntake';
import {
  mergePDFs,
  splitPDF,
  compressPDFLow,
  compressPDFFromImages,
  imagesToPDF,
  parsePageRange,
  formatFileSize,
} from '@/lib/pdfOperations';
import JSZip from 'jszip';
import { compressPDF as nativeCompress, protectPDF as nativeProtect } from '@/lib/nativebridge';
import { classifyCloudError } from '@/lib/cloudError';
import { ProGate } from '@/components/ProBadge';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

// ==================== Operations ====================

const OPERATIONS = [
  {
    id: 'merge',
    label: 'Merge All',
    icon: Layers,
    description: 'Combine all PDFs into one file',
    accept: '.pdf,application/pdf',
    fileType: 'pdf',
  },
  {
    id: 'compress',
    label: 'Compress',
    icon: Minimize2,
    description: 'Compress each PDF individually',
    accept: '.pdf,application/pdf',
    fileType: 'pdf',
  },
  {
    id: 'to-pdf',
    label: 'Convert to PDF',
    icon: FileText,
    description: 'Convert images to PDFs',
    accept: 'image/jpeg,image/png,image/webp,image/bmp,image/tiff',
    fileType: 'image',
  },
  {
    id: 'to-images',
    label: 'Convert to Images',
    icon: ImageIcon,
    description: 'Convert each PDF to images',
    accept: '.pdf,application/pdf',
    fileType: 'pdf',
  },
  {
    id: 'protect',
    label: 'Protect',
    icon: Lock,
    description: 'Password-protect all PDFs',
    accept: '.pdf,application/pdf',
    fileType: 'pdf',
  },
  {
    id: 'split',
    label: 'Split',
    icon: Scissors,
    description: 'Split each PDF by a rule',
    accept: '.pdf,application/pdf',
    fileType: 'pdf',
  },
];

// ==================== Option constants ====================

// Tail of the intake message for a locked (user-password) PDF, per operation.
const LOCKED_ACTION = {
  merge: ['merge it', 'merge them'],
  compress: ['compress it', 'compress them'],
  'to-images': ['convert it', 'convert them'],
  protect: ['protect it', 'protect them'],
  split: ['split it', 'split them'],
};

const COMPRESS_LEVELS = [
  { id: 'low', label: 'Low', desc: 'Strip metadata & optimize structure' },
  { id: 'medium', label: 'Medium', desc: 'Reduce image quality + optimize', recommended: true },
  { id: 'high', label: 'High', desc: 'Maximum compression' },
];

const PAGE_SIZES = [
  { id: 'fit', label: 'Fit to Image' },
  { id: 'a4', label: 'A4' },
  { id: 'letter', label: 'Letter' },
];

const ORIENTATIONS = [
  { id: 'auto', label: 'Auto' },
  { id: 'portrait', label: 'Portrait' },
  { id: 'landscape', label: 'Landscape' },
];

const MARGINS = [
  { id: 'none', label: 'None' },
  { id: 'small', label: 'Small' },
  { id: 'normal', label: 'Normal' },
];

const IMG_FORMATS = [
  { id: 'png', label: 'PNG' },
  { id: 'jpg', label: 'JPG' },
];

const IMG_QUALITIES = [
  { id: 0.6, label: 'Low' },
  { id: 0.8, label: 'Medium' },
  { id: 0.95, label: 'High' },
];

const DPI_OPTIONS = [
  { id: 72, label: '72 DPI' },
  { id: 150, label: '150 DPI' },
  { id: 300, label: '300 DPI' },
];

const SPLIT_MODES = [
  { id: 'every-n', label: 'Every N pages' },
  { id: 'range', label: 'Extract page range' },
];

const COMPRESS_SCALE = { medium: 0.8, high: 0.6 };
const COMPRESS_QUALITY = { medium: 0.65, high: 0.40 };

let fileIdCounter = 0;

// ==================== Batch Page ====================

export default function Batch() {
  const { openFiles, saveFile: saveFileDialog, openFolder, desktop } = useFileDialog();

  // Wizard state
  const [step, setStep] = useState(1);
  const [operation, setOperation] = useState(null);
  const [files, setFiles] = useState([]);
  const [intakeError, setIntakeError] = useState(null); // locked PDFs rejected at intake
  const [isDragging, setIsDragging] = useState(false);

  // Options state
  const [compressLevel, setCompressLevel] = useState('medium');
  const [pageSize, setPageSize] = useState('a4');
  const [orientation, setOrientation] = useState('auto');
  const [margin, setMargin] = useState('small');
  const [combineImages, setCombineImages] = useState(false);
  const [imgFormat, setImgFormat] = useState('png');
  const [imgQuality, setImgQuality] = useState(0.8);
  const [imgDpi, setImgDpi] = useState(150);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [splitMode, setSplitMode] = useState('every-n');
  const [splitN, setSplitN] = useState(1);
  const [splitRange, setSplitRange] = useState('');
  const [outputFilename, setOutputFilename] = useState('merged.pdf');

  // Drag reorder
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Processing state
  const [processing, setProcessing] = useState(false);
  const [fileStatuses, setFileStatuses] = useState({}); // { [fileId]: { status, error, resultBlob, resultName } }
  const [overallProgress, setOverallProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState(null); // set when processing completes
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);

  const op = OPERATIONS.find((o) => o.id === operation);

  // ==================== File handling ====================

  const addFiles = useCallback(
    async (fileList) => {
      if (!op) return;
      const validFiles = Array.from(fileList).filter((f) => {
        if (op.fileType === 'pdf') return f.type === 'application/pdf';
        if (op.fileType === 'image') return f.type.startsWith('image/');
        return true;
      });
      if (validFiles.length === 0) return;

      const locked = [];
      const entries = (await Promise.all(
        validFiles.map(async (f) => {
          const entry = {
            id: `bf-${++fileIdCounter}`,
            file: f,
            name: f.name,
            size: f.size,
            pageCount: null,
            thumbnailUrl: null,
          };

          if (op.fileType === 'pdf') {
            // Every PDF operation here reaches pdf-lib (merge/split directly,
            // compress as the fallback), which refuses encrypted input —
            // including owner-password-only files PDF.js renders happily.
            // Decrypt losslessly at intake and carry the readable bytes on
            // the entry so each operation reads plain input; a truly locked
            // file is reported and not queued.
            let buf = null;
            try {
              const raw = await f.arrayBuffer();
              buf = await ensurePdfLibReadable(raw);
              if (buf !== raw) entry.file = new File([buf], f.name, { type: 'application/pdf' });
            } catch (err) {
              if (err instanceof PdfLockedError) {
                locked.push(f.name);
                return null;
              }
              // Unreadable by pdf-lib for another reason — keep the original
              // file and let the thumbnail / operation report it, as before.
            }
            try {
              if (!buf) buf = await f.arrayBuffer();
              const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
              entry.pageCount = doc.numPages;

              // Generate thumbnail from page 1
              const page = await doc.getPage(1);
              const vp = page.getViewport({ scale: 0.15 });
              const canvas = document.createElement('canvas');
              canvas.width = vp.width;
              canvas.height = vp.height;
              await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
              entry.thumbnailUrl = canvas.toDataURL('image/jpeg', 0.6);
              doc.destroy();
            } catch {
              entry.pageCount = 0;
            }
          } else if (op.fileType === 'image') {
            entry.thumbnailUrl = URL.createObjectURL(f);
          }

          return entry;
        })
      )).filter(Boolean);

      const action = LOCKED_ACTION[op.id] || ['process it', 'process them'];
      setIntakeError(locked.length ? pdfLockedFilesMessage(locked, action[locked.length > 1 ? 1 : 0]) : null);
      setFiles((prev) => [...prev, ...entries]);
    },
    [op]
  );

  const handleBrowseFiles = useCallback(async () => {
    if (!op) return;
    const results = await openFiles(op.accept);
    if (results.length === 0) return;
    const fileList = results.map((r) => {
      const mimeType = op.fileType === 'pdf' ? 'application/pdf' : 'image/*';
      return new File([r.buffer], r.name, { type: mimeType });
    });
    addFiles(fileList);
  }, [op, openFiles, addFiles]);

  const removeFile = useCallback((id) => {
    setFiles((prev) => {
      const removed = prev.find((f) => f.id === id);
      if (removed?.thumbnailUrl && removed.file.type.startsWith('image/')) {
        URL.revokeObjectURL(removed.thumbnailUrl);
      }
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  // Drag reorder handlers
  const handleDragStart = useCallback((e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setDragImage(e.currentTarget, 20, 20);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      setFiles((prev) => {
        const next = [...prev];
        const [dragged] = next.splice(dragIndex, 1);
        next.splice(dragOverIndex, 0, dragged);
        return next;
      });
    }
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, dragOverIndex]);

  const handleDragOver = useCallback(
    (e, index) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (index !== dragOverIndex) setDragOverIndex(index);
    },
    [dragOverIndex]
  );

  const displayFiles = useMemo(() => {
    if (dragIndex === null || dragOverIndex === null || dragIndex === dragOverIndex) return files;
    const reordered = [...files];
    const [dragged] = reordered.splice(dragIndex, 1);
    reordered.splice(dragOverIndex, 0, dragged);
    return reordered;
  }, [files, dragIndex, dragOverIndex]);

  // ==================== Processing ====================

  const processFiles = useCallback(async () => {
    if (files.length === 0 || !operation) return;

    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }

    setProcessing(true);
    const statuses = {};
    files.forEach((f) => {
      statuses[f.id] = { status: 'pending', error: null, resultBlob: null, resultName: null };
    });
    setFileStatuses({ ...statuses });
    setOverallProgress({ done: 0, total: files.length });

    const completedResults = [];
    let totalSizeBefore = 0;
    let totalSizeAfter = 0;
    let errorCount = 0;
    let rateLimited = false; // anonymous cloud /compress hit its per-IP daily cap

    try {
      // === MERGE ALL ===
      if (operation === 'merge') {
        // Mark all as processing
        files.forEach((f) => {
          statuses[f.id] = { ...statuses[f.id], status: 'processing' };
        });
        setFileStatuses({ ...statuses });

        const sources = [];
        for (let i = 0; i < files.length; i++) {
          try {
            const buf = await files[i].file.arrayBuffer();
            const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
            const allPages = new Set(Array.from({ length: doc.numPages }, (_, j) => j + 1));
            sources.push({ arrayBuffer: buf, selectedPages: allPages });
            doc.destroy();
            statuses[files[i].id] = { ...statuses[files[i].id], status: 'done' };
          } catch (err) {
            statuses[files[i].id] = { ...statuses[files[i].id], status: 'error', error: err.message };
            errorCount++;
          }
          setFileStatuses({ ...statuses });
          setOverallProgress({ done: i + 1, total: files.length });
        }

        if (sources.length > 0) {
          const mergedBytes = await mergePDFs(sources);
          const blob = new Blob([mergedBytes], { type: 'application/pdf' });
          completedResults.push({ blob, name: outputFilename || 'merged.pdf' });
          totalSizeAfter = blob.size;
          totalSizeBefore = files.reduce((s, f) => s + f.size, 0);
        }
      }

      // === COMPRESS ===
      else if (operation === 'compress') {
        for (let i = 0; i < files.length; i++) {
          const f = files[i];
          statuses[f.id] = { ...statuses[f.id], status: 'processing' };
          setFileStatuses({ ...statuses });
          totalSizeBefore += f.size;

          try {
            const buf = await f.file.arrayBuffer();
            let compressedBytes;

            // Try server-side qpdf (desktop native or cloud API)
            try {
              const nativeResult = await nativeCompress(buf, compressLevel);
              if (nativeResult) {
                compressedBytes = new Uint8Array(nativeResult);
              }
            } catch (cloudErr) {
              // Anonymous user hit the per-IP daily cap (HTTP 429). Surface the
              // sign-up nudge and STOP the batch — do NOT degrade to the pdf-lib
              // fallback (a 0% "result" that masks the cap), as Compress.jsx
              // does. Genuine network/offline errors fall through to the browser
              // fallback below.
              if (classifyCloudError(cloudErr) === 'rate_limited') {
                rateLimited = true;
                totalSizeBefore -= f.size; // this file never got processed
                statuses[f.id] = { ...statuses[f.id], status: 'pending' };
                break;
              }
              // Cloud/native unavailable — fall through to browser compression
            }

            // Browser fallback
            if (!compressedBytes) {
              if (compressLevel === 'low') {
                compressedBytes = await compressPDFLow(buf);
              } else {
                const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
                const scale = COMPRESS_SCALE[compressLevel];
                const quality = COMPRESS_QUALITY[compressLevel];
                const pageImages = [];

                for (let p = 1; p <= doc.numPages; p++) {
                  const page = await doc.getPage(p);
                  const origVp = page.getViewport({ scale: 1 });
                  const renderVp = page.getViewport({ scale });
                  const canvas = document.createElement('canvas');
                  canvas.width = renderVp.width;
                  canvas.height = renderVp.height;
                  await page.render({ canvasContext: canvas.getContext('2d'), viewport: renderVp }).promise;
                  const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', quality));
                  pageImages.push({
                    pdfWidth: origVp.width,
                    pdfHeight: origVp.height,
                    jpegBytes: new Uint8Array(await blob.arrayBuffer()),
                  });
                }
                doc.destroy();
                compressedBytes = await compressPDFFromImages(pageImages, buf);
              }
            }

            const blob = new Blob([compressedBytes], { type: 'application/pdf' });
            const baseName = f.name.replace(/\.pdf$/i, '');
            totalSizeAfter += blob.size;
            statuses[f.id] = { ...statuses[f.id], status: 'done', resultBlob: blob, resultName: `${baseName}_compressed.pdf` };
            completedResults.push({ blob, name: `${baseName}_compressed.pdf` });
          } catch (err) {
            statuses[f.id] = { ...statuses[f.id], status: 'error', error: err.message };
            errorCount++;
          }

          setFileStatuses({ ...statuses });
          setOverallProgress({ done: i + 1, total: files.length });
        }

        // Stopped early on a 429 — nudge sign-up. Any files compressed before
        // the cap are still finalized below so the user keeps that work.
        if (rateLimited) setShowFreeTierModal(true);
      }

      // === CONVERT TO PDF ===
      else if (operation === 'to-pdf') {
        if (combineImages) {
          // All images into one PDF
          files.forEach((f) => {
            statuses[f.id] = { ...statuses[f.id], status: 'processing' };
          });
          setFileStatuses({ ...statuses });

          try {
            const images = await Promise.all(
              files.map(async (f) => {
                const dataUrl = await readFileAsDataUrl(f.file);
                const normalized = await normalizeImage(dataUrl);
                const dims = await getImageDimensions(normalized);
                return { dataUrl: normalized, width: dims.width, height: dims.height };
              })
            );

            const bytes = await imagesToPDF(images, { pageSize, orientation, margin });
            const blob = new Blob([bytes], { type: 'application/pdf' });
            completedResults.push({ blob, name: 'images_combined.pdf' });

            files.forEach((f) => {
              statuses[f.id] = { ...statuses[f.id], status: 'done' };
            });
          } catch (err) {
            files.forEach((f) => {
              statuses[f.id] = { ...statuses[f.id], status: 'error', error: err.message };
            });
            errorCount = files.length;
          }

          setFileStatuses({ ...statuses });
          setOverallProgress({ done: files.length, total: files.length });
        } else {
          // One PDF per image
          for (let i = 0; i < files.length; i++) {
            const f = files[i];
            statuses[f.id] = { ...statuses[f.id], status: 'processing' };
            setFileStatuses({ ...statuses });

            try {
              const dataUrl = await readFileAsDataUrl(f.file);
              const normalized = await normalizeImage(dataUrl);
              const dims = await getImageDimensions(normalized);
              const bytes = await imagesToPDF(
                [{ dataUrl: normalized, width: dims.width, height: dims.height }],
                { pageSize, orientation, margin }
              );
              const blob = new Blob([bytes], { type: 'application/pdf' });
              const name = f.name.replace(/\.[^.]+$/, '.pdf');
              statuses[f.id] = { ...statuses[f.id], status: 'done', resultBlob: blob, resultName: name };
              completedResults.push({ blob, name });
            } catch (err) {
              statuses[f.id] = { ...statuses[f.id], status: 'error', error: err.message };
              errorCount++;
            }

            setFileStatuses({ ...statuses });
            setOverallProgress({ done: i + 1, total: files.length });
          }
        }
      }

      // === CONVERT TO IMAGES ===
      else if (operation === 'to-images') {
        const scale = imgDpi / 72;
        const mimeType = imgFormat === 'png' ? 'image/png' : 'image/jpeg';
        const ext = imgFormat === 'png' ? 'png' : 'jpg';

        for (let i = 0; i < files.length; i++) {
          const f = files[i];
          statuses[f.id] = { ...statuses[f.id], status: 'processing' };
          setFileStatuses({ ...statuses });

          try {
            const buf = await f.file.arrayBuffer();
            const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
            const baseName = f.name.replace(/\.pdf$/i, '');
            const zip = new JSZip();

            for (let p = 1; p <= doc.numPages; p++) {
              const page = await doc.getPage(p);
              const vp = page.getViewport({ scale });
              const canvas = document.createElement('canvas');
              canvas.width = vp.width;
              canvas.height = vp.height;
              await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
              const blob = await new Promise((r) =>
                canvas.toBlob(r, mimeType, imgFormat === 'jpg' ? imgQuality : undefined)
              );
              zip.file(`${baseName}_page${p}.${ext}`, await blob.arrayBuffer());
            }

            doc.destroy();
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            statuses[f.id] = { ...statuses[f.id], status: 'done', resultBlob: zipBlob, resultName: `${baseName}_images.zip` };
            completedResults.push({ blob: zipBlob, name: `${baseName}_images.zip` });
          } catch (err) {
            statuses[f.id] = { ...statuses[f.id], status: 'error', error: err.message };
            errorCount++;
          }

          setFileStatuses({ ...statuses });
          setOverallProgress({ done: i + 1, total: files.length });
        }
      }

      // === PROTECT ===
      else if (operation === 'protect') {
        for (let i = 0; i < files.length; i++) {
          const f = files[i];
          statuses[f.id] = { ...statuses[f.id], status: 'processing' };
          setFileStatuses({ ...statuses });

          try {
            const buf = await f.file.arrayBuffer();
            // Batch has no advanced-permissions UI (password/confirm only) —
            // use the same sensible defaults as Protect.jsx's initial state.
            const result = await nativeProtect(buf, {
              password,
              permissions: { print: true, copy: true, edit: false },
            });

            if (!result) {
              // Desktop with no cloud + no local engine available.
              statuses[f.id] = {
                ...statuses[f.id],
                status: 'error',
                error: 'Password protection needs an internet connection or the offline engine pack. Connect and try again.',
              };
              errorCount++;
            } else {
              const bytes = new Uint8Array(result);
              const blob = new Blob([bytes], { type: 'application/pdf' });
              const baseName = f.name.replace(/\.pdf$/i, '');
              const name = `${baseName} (protected).pdf`;
              statuses[f.id] = { ...statuses[f.id], status: 'done', resultBlob: blob, resultName: name };
              completedResults.push({ blob, name });
            }
          } catch (err) {
            const msg = err.message || '';
            let readable;
            if (/signed in/i.test(msg)) {
              readable = 'Sign in (free) to password-protect PDFs — encryption runs on our secure server.';
            } else if (err.code === 'already_encrypted' || /already password-protected/i.test(msg)) {
              readable = 'This file is already password-protected. Unlock it first, then set a new password.';
            } else {
              readable = `Protection failed: ${msg}`;
            }
            statuses[f.id] = { ...statuses[f.id], status: 'error', error: readable };
            errorCount++;
          }

          setFileStatuses({ ...statuses });
          setOverallProgress({ done: i + 1, total: files.length });
        }
      }

      // === SPLIT ===
      else if (operation === 'split') {
        for (let i = 0; i < files.length; i++) {
          const f = files[i];
          statuses[f.id] = { ...statuses[f.id], status: 'processing' };
          setFileStatuses({ ...statuses });

          try {
            const buf = await f.file.arrayBuffer();
            const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
            const numPages = doc.numPages;
            doc.destroy();
            const baseName = f.name.replace(/\.pdf$/i, '');

            if (splitMode === 'range') {
              const pageNumbers = parsePageRange(splitRange, numPages);
              if (pageNumbers.length === 0) throw new Error('No valid pages in range');
              const bytes = await splitPDF(buf, pageNumbers);
              const blob = new Blob([bytes], { type: 'application/pdf' });
              statuses[f.id] = { ...statuses[f.id], status: 'done', resultBlob: blob, resultName: `${baseName}_extracted.pdf` };
              completedResults.push({ blob, name: `${baseName}_extracted.pdf` });
            } else {
              // every-n: split into chunks
              const n = Math.max(1, splitN);
              const chunks = [];
              for (let p = 1; p <= numPages; p += n) {
                const chunk = [];
                for (let j = p; j < p + n && j <= numPages; j++) chunk.push(j);
                chunks.push(chunk);
              }

              if (chunks.length === 1) {
                const bytes = await splitPDF(buf, chunks[0]);
                const blob = new Blob([bytes], { type: 'application/pdf' });
                statuses[f.id] = { ...statuses[f.id], status: 'done', resultBlob: blob, resultName: `${baseName}_split.pdf` };
                completedResults.push({ blob, name: `${baseName}_split.pdf` });
              } else {
                const zip = new JSZip();
                for (let ci = 0; ci < chunks.length; ci++) {
                  const bytes = await splitPDF(buf, chunks[ci]);
                  zip.file(`${baseName}_part${ci + 1}.pdf`, bytes);
                }
                const zipBlob = await zip.generateAsync({ type: 'blob' });
                statuses[f.id] = { ...statuses[f.id], status: 'done', resultBlob: zipBlob, resultName: `${baseName}_split.zip` };
                completedResults.push({ blob: zipBlob, name: `${baseName}_split.zip` });
              }
            }
          } catch (err) {
            statuses[f.id] = { ...statuses[f.id], status: 'error', error: err.message };
            errorCount++;
          }

          setFileStatuses({ ...statuses });
          setOverallProgress({ done: i + 1, total: files.length });
        }
      }
    } catch (err) {
      // Catch-all for unexpected errors
      console.error('Batch processing error:', err);
    }

    setProcessing(false);
    if (usageCapApplies()) incrementUsage();
    setResults({
      items: completedResults,
      errorCount,
      totalSizeBefore,
      totalSizeAfter,
      rateLimited,
    });
  }, [
    files, operation, compressLevel, pageSize, orientation, margin,
    combineImages, imgFormat, imgQuality, imgDpi, password,
    splitMode, splitN, splitRange, outputFilename,
  ]);

  // ==================== Download ====================

  const downloadSingle = useCallback(async (blob, name) => {
    await saveFileDialog(blob, name);
  }, [saveFileDialog]);

  const downloadAllAsZip = useCallback(async () => {
    if (!results?.items?.length) return;

    // Desktop: offer "Save to Folder" — save each file individually
    if (desktop) {
      const folderPath = await openFolder();
      if (folderPath) {
        for (const item of results.items) {
          const bytes = new Uint8Array(await item.blob.arrayBuffer());
          await window.electronAPI.saveFileToFolder(bytes, item.name, folderPath);
        }
        return;
      }
      // User cancelled folder picker — fall through to ZIP
    }

    const zip = new JSZip();
    for (const item of results.items) {
      zip.file(item.name, await item.blob.arrayBuffer());
    }
    const zipNames = {
      merge: 'merged_files.zip',
      compress: 'compressed_files.zip',
      'to-pdf': 'converted_pdfs.zip',
      'to-images': 'converted_images.zip',
      protect: 'protected_files.zip',
      split: 'split_files.zip',
    };
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    await saveFileDialog(zipBlob, zipNames[operation] || 'batch_results.zip');
  }, [results, operation, saveFileDialog, desktop, openFolder]);

  // ==================== Reset ====================

  const resetAll = useCallback(() => {
    // Clean up image object URLs
    files.forEach((f) => {
      if (f.thumbnailUrl && f.file.type.startsWith('image/')) {
        URL.revokeObjectURL(f.thumbnailUrl);
      }
    });
    setStep(1);
    setOperation(null);
    setFiles([]);
    setFileStatuses({});
    setOverallProgress({ done: 0, total: 0 });
    setResults(null);
    setProcessing(false);
    setPassword('');
    setConfirmPassword('');
    setSplitRange('');
    setOutputFilename('merged.pdf');
  }, [files]);

  // ==================== Derived ====================

  const totalSize = useMemo(() => files.reduce((s, f) => s + f.size, 0), [files]);
  const totalPages = useMemo(
    () => files.reduce((s, f) => s + (f.pageCount || 0), 0),
    [files]
  );

  const canProceedToStep3 = files.length >= (operation === 'merge' ? 2 : 1);
  const canProcess =
    operation === 'protect'
      ? password.length > 0 && password === confirmPassword
      : true;

  // ==================== Render ====================

  return (
    <ProGate feature="batch">
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900">Batch Processing</h1>
            <p className="text-sm text-gray-500">Apply operations to multiple files at once</p>
          </div>
          {step > 1 && !processing && !results && (
            <button onClick={resetAll} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors">
              <RotateCcw size={14} />
              Start Over
            </button>
          )}
        </div>

        {/* Step indicator */}
        <div className="max-w-4xl mx-auto mt-4">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    s < step
                      ? 'bg-green-500 text-white'
                      : s === step
                        ? 'bg-brand-blue text-white'
                        : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s < step ? <CheckCircle size={14} /> : s}
                </div>
                {s < 4 && <div className={`flex-1 h-0.5 rounded ${s < step ? 'bg-green-500' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
          <div className="flex mt-1.5">
            <span className="flex-1 text-[10px] text-gray-500">Operation</span>
            <span className="flex-1 text-[10px] text-gray-500">Upload</span>
            <span className="flex-1 text-[10px] text-gray-500">Options</span>
            <span className="text-[10px] text-gray-500">Process</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto w-full p-6 flex-1 flex flex-col">
        {/* ==================== Step 1: Choose Operation ==================== */}
        {step === 1 && (
          <>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Choose an operation</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {OPERATIONS.map((o) => (
                <button
                  key={o.id}
                  onClick={() => {
                    setOperation(o.id);
                    setFiles([]);
                    setStep(2);
                  }}
                  className={`flex flex-col items-center p-6 rounded-xl border-2 transition-all cursor-pointer ${
                    operation === o.id
                      ? 'border-brand-blue bg-white shadow-sm'
                      : 'border-gray-200 bg-white hover:border-brand-blue hover:shadow-md'
                  }`}
                >
                  <div className="w-12 h-12 bg-brand-light rounded-lg flex items-center justify-center mb-3">
                    <o.icon size={22} className="text-brand-blue" />
                  </div>
                  <h3 className="font-semibold text-gray-900">{o.label}</h3>
                  <p className="text-sm text-gray-500 mt-1 text-center">{o.description}</p>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ==================== Step 2: Upload Files ==================== */}
        {step === 2 && op && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Upload {op.fileType === 'pdf' ? 'PDFs' : 'images'}
              </h2>
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft size={14} />
                Back
              </button>
            </div>

            {/* Drop zone */}
            <div
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                addFiles(e.dataTransfer.files);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onClick={handleBrowseFiles}
              className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all mb-4 ${
                isDragging
                  ? 'border-brand-blue bg-brand-light/30 scale-[1.01]'
                  : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
              }`}
            >
              <Upload
                size={32}
                className={`mx-auto mb-2 ${isDragging ? 'text-brand-blue' : 'text-gray-400'}`}
              />
              <p className="text-base font-medium text-gray-700">
                {files.length > 0 ? 'Add more files' : `Drop ${op.fileType === 'pdf' ? 'PDFs' : 'images'} here, or click to browse`}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                {op.fileType === 'pdf' ? 'PDF files' : 'JPG, PNG, WEBP, BMP, TIFF'}
              </p>
            </div>

            {op.fileType === 'pdf' && (
              <CloudSourceHint
                multiple
                onPicked={(results) => {
                  const fileList = results.map((r) => new File([r.buffer], r.name, { type: 'application/pdf' }));
                  addFiles(fileList);
                }}
                className="mb-4"
              />
            )}

            {intakeError && <p role="alert" className="text-red-600 text-sm mb-4">{intakeError}</p>}

            {/* File list */}
            {files.length > 0 && (
              <>
                {files.length >= 10 && (
                  <div className="flex items-center gap-2 p-3 mb-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle size={16} className="text-amber-600 shrink-0" />
                    <p className="text-sm text-amber-800">
                      Large batch ({files.length} files) — processing may take a while.
                    </p>
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  {displayFiles.map((f, index) => {
                    const realIndex = files.indexOf(f);
                    const isBeingDragged = realIndex === dragIndex;
                    const isDropTarget =
                      dragIndex !== null &&
                      dragOverIndex !== null &&
                      index === dragOverIndex &&
                      dragIndex !== dragOverIndex;

                    return (
                      <div
                        key={f.id}
                        draggable={operation === 'merge'}
                        onDragStart={operation === 'merge' ? (e) => handleDragStart(e, realIndex) : undefined}
                        onDragEnd={operation === 'merge' ? handleDragEnd : undefined}
                        onDragOver={operation === 'merge' ? (e) => handleDragOver(e, index) : undefined}
                        className={`bg-white rounded-xl border-2 p-3 flex items-center gap-3 transition-all duration-150 ${
                          isBeingDragged
                            ? 'opacity-25 scale-[0.98] border-gray-200'
                            : isDropTarget
                              ? 'border-brand-blue ring-2 ring-brand-blue/20'
                              : 'border-gray-200 hover:shadow-sm'
                        }`}
                      >
                        {operation === 'merge' && (
                          <div className="cursor-grab active:cursor-grabbing text-gray-400">
                            <GripVertical size={16} />
                          </div>
                        )}
                        {f.thumbnailUrl && (
                          <img
                            src={f.thumbnailUrl}
                            alt={f.name}
                            className="w-10 h-12 object-cover rounded border border-gray-200"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{f.name}</p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(f.size)}
                            {f.pageCount != null && f.pageCount > 0 && ` · ${f.pageCount} page${f.pageCount !== 1 ? 's' : ''}`}
                          </p>
                        </div>
                        <button
                          onClick={() => removeFile(f.id)}
                          className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Summary bar */}
                <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4">
                  <span className="text-sm text-gray-600">
                    {files.length} file{files.length !== 1 ? 's' : ''}
                    {totalPages > 0 && ` · ${totalPages} pages`}
                    {' · '}
                    {formatFileSize(totalSize)}
                  </span>
                  <button
                    onClick={handleBrowseFiles}
                    className="text-sm font-medium text-brand-blue hover:underline"
                  >
                    Add More
                  </button>
                </div>

                {/* Next button */}
                <button
                  onClick={() => setStep(3)}
                  disabled={!canProceedToStep3}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Configure Options
                  <ArrowRight size={16} />
                </button>
                {operation === 'merge' && files.length < 2 && (
                  <p className="text-xs text-gray-400 mt-2 text-center">
                    Add at least 2 files to merge
                  </p>
                )}
              </>
            )}

            {files.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <op.icon size={48} className="mx-auto mb-4 opacity-50" />
                <p>Upload files to get started</p>
              </div>
            )}
          </>
        )}

        {/* ==================== Step 3: Configure Options ==================== */}
        {step === 3 && !processing && !results && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Configure Options</h2>
              <button
                onClick={() => setStep(2)}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft size={14} />
                Back
              </button>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
              {/* Merge options */}
              {operation === 'merge' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Output filename</label>
                  <input
                    type="text"
                    value={outputFilename}
                    onChange={(e) => setOutputFilename(e.target.value)}
                    className="w-full h-10 px-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue"
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Files will be merged in the order shown in Step 2. Go back to reorder.
                  </p>
                </div>
              )}

              {/* Compress options */}
              {operation === 'compress' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Compression level</label>
                  <div className="grid grid-cols-3 gap-3">
                    {COMPRESS_LEVELS.map((l) => (
                      <button
                        key={l.id}
                        onClick={() => setCompressLevel(l.id)}
                        className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                          compressLevel === l.id
                            ? 'border-brand-blue bg-white shadow-sm'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        {l.recommended && (
                          <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-medium bg-brand-blue text-white px-2 py-0.5 rounded-full">
                            Recommended
                          </span>
                        )}
                        <p className="font-semibold text-gray-900 text-sm">{l.label}</p>
                        <p className="text-xs text-gray-500 mt-1">{l.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Convert to PDF options */}
              {operation === 'to-pdf' && (
                <div className="space-y-5">
                  <div>
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={combineImages}
                        onChange={(e) => setCombineImages(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue"
                      />
                      <span className="text-sm text-gray-700">Combine all images into one PDF</span>
                    </label>
                    <p className="text-xs text-gray-400 mt-1 ml-6.5">
                      {combineImages
                        ? 'All images will be pages in a single PDF'
                        : 'Each image will become a separate PDF file'}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <OptionGroup label="Page Size" options={PAGE_SIZES} value={pageSize} onChange={setPageSize} />
                    <OptionGroup label="Orientation" options={ORIENTATIONS} value={orientation} onChange={setOrientation} disabled={pageSize === 'fit'} />
                    <OptionGroup label="Margin" options={MARGINS} value={margin} onChange={setMargin} disabled={pageSize === 'fit'} />
                  </div>
                </div>
              )}

              {/* Convert to Images options */}
              {operation === 'to-images' && (
                <div className="grid grid-cols-3 gap-4">
                  <OptionGroup label="Format" options={IMG_FORMATS} value={imgFormat} onChange={setImgFormat} />
                  <OptionGroup label="Quality" options={IMG_QUALITIES} value={imgQuality} onChange={setImgQuality} disabled={imgFormat === 'png'} />
                  <OptionGroup label="Resolution" options={DPI_OPTIONS} value={imgDpi} onChange={setImgDpi} />
                </div>
              )}

              {/* Protect options */}
              {operation === 'protect' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter a password"
                      className="w-full h-10 px-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter password"
                      className={`w-full h-10 px-4 text-sm border rounded-lg focus:outline-none focus:border-brand-blue ${
                        confirmPassword && password !== confirmPassword ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {confirmPassword && password !== confirmPassword && (
                      <p className="text-xs text-red-500 mt-1">Passwords don't match</p>
                    )}
                  </div>
                </div>
              )}

              {/* Split options */}
              {operation === 'split' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Split rule</label>
                    <div className="flex gap-2">
                      {SPLIT_MODES.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => setSplitMode(m.id)}
                          className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                            splitMode === m.id
                              ? 'bg-brand-blue text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {splitMode === 'every-n' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Split every N pages
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={splitN}
                        onChange={(e) => setSplitN(parseInt(e.target.value) || 1)}
                        className="w-32 h-10 px-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Each PDF will be split into parts of {splitN} page{splitN !== 1 ? 's' : ''} each
                      </p>
                    </div>
                  )}

                  {splitMode === 'range' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Page range to extract
                      </label>
                      <input
                        type="text"
                        value={splitRange}
                        onChange={(e) => setSplitRange(e.target.value)}
                        placeholder="e.g. 1-3, 5, 8-12"
                        className="w-full h-10 px-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        This range will be extracted from each PDF
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Process button */}
            <button
              onClick={() => {
                setStep(4);
                processFiles();
              }}
              disabled={!canProcess}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Process {files.length} File{files.length !== 1 ? 's' : ''}
              <ArrowRight size={16} />
            </button>
          </>
        )}

        {/* ==================== Step 4: Processing & Results ==================== */}
        {step === 4 && (
          <>
                {/* Overall progress */}
                {processing && (
                  <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Loader2 size={18} className="animate-spin text-brand-blue" />
                      <span className="text-sm font-medium text-gray-700">
                        Processing {overallProgress.done} of {overallProgress.total} files…
                      </span>
                    </div>
                    <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-blue rounded-full transition-all duration-300"
                        style={{
                          width: `${overallProgress.total > 0 ? (overallProgress.done / overallProgress.total) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Results summary */}
                {results && (
                  <div className={`bg-white rounded-xl border p-5 mb-4 ${results.rateLimited ? 'border-amber-200' : 'border-green-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {results.rateLimited ? (
                        <AlertTriangle size={20} className="text-amber-500" />
                      ) : (
                        <CheckCircle size={20} className="text-green-500" />
                      )}
                      <span className="text-base font-semibold text-gray-900">
                        {results.rateLimited ? 'Daily free limit reached' : 'Processing complete'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <span>{results.items.length} file{results.items.length !== 1 ? 's' : ''} processed</span>
                      {results.errorCount > 0 && (
                        <span className="text-red-600">{results.errorCount} error{results.errorCount !== 1 ? 's' : ''}</span>
                      )}
                      {operation === 'compress' && results.totalSizeBefore > 0 && results.totalSizeAfter > 0 && (
                        <span className="text-green-600">
                          {Math.round((1 - results.totalSizeAfter / results.totalSizeBefore) * 100)}% size reduction
                          ({formatFileSize(results.totalSizeBefore)} → {formatFileSize(results.totalSizeAfter)})
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Per-file statuses */}
                <div className="space-y-2 mb-4">
                  {files.map((f) => {
                    const s = fileStatuses[f.id];
                    if (!s) return null;
                    return (
                      <div
                        key={f.id}
                        className="bg-white rounded-xl border border-gray-200 p-3 flex items-center gap-3"
                      >
                        {f.thumbnailUrl && (
                          <img
                            src={f.thumbnailUrl}
                            alt={f.name}
                            className="w-8 h-10 object-cover rounded border border-gray-200"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{f.name}</p>
                          {s.error && <p className="text-xs text-red-500 truncate">{s.error}</p>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {s.status === 'pending' && (
                            <span className="text-xs text-gray-400">Waiting</span>
                          )}
                          {s.status === 'processing' && (
                            <Loader2 size={16} className="animate-spin text-brand-blue" />
                          )}
                          {s.status === 'done' && (
                            <CheckCircle size={16} className="text-green-500" />
                          )}
                          {s.status === 'error' && (
                            <XCircle size={16} className="text-red-500" />
                          )}
                          {s.status === 'done' && s.resultBlob && (
                            <button
                              onClick={() => downloadSingle(s.resultBlob, s.resultName)}
                              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                              title={desktop ? 'Save' : 'Download'}
                            >
                              {desktop ? <Save size={14} /> : <Download size={14} />}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Download buttons */}
                {results && results.items.length > 0 && (
                  <div className="flex gap-3">
                    {results.items.length === 1 ? (
                      <button
                        onClick={() => downloadSingle(results.items[0].blob, results.items[0].name)}
                        className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors"
                      >
                        {desktop ? <Save size={16} /> : <Download size={16} />}
                        {desktop ? 'Save' : 'Download'} {results.items[0].name}
                      </button>
                    ) : (
                      <>
                        {desktop && (
                          <button
                            onClick={downloadAllAsZip}
                            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors"
                          >
                            <Save size={16} />
                            Save to Folder
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            const zip = new JSZip();
                            for (const item of results.items) {
                              zip.file(item.name, await item.blob.arrayBuffer());
                            }
                            const zipNames = {
                              merge: 'merged_files.zip',
                              compress: 'compressed_files.zip',
                              'to-pdf': 'converted_pdfs.zip',
                              'to-images': 'converted_images.zip',
                              protect: 'protected_files.zip',
                              split: 'split_files.zip',
                            };
                            const zipBlob = await zip.generateAsync({ type: 'blob' });
                            await saveFileDialog(zipBlob, zipNames[operation] || 'batch_results.zip');
                          }}
                          className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 ${desktop ? 'border border-gray-300 text-gray-700 hover:bg-gray-50' : 'bg-brand-blue text-white hover:bg-brand-dark'} rounded-lg font-medium transition-colors`}
                        >
                          <Archive size={16} />
                          {desktop ? 'Save' : 'Download'} All as ZIP
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Start over */}
                {results && (
                  <button
                    onClick={resetAll}
                    className="w-full mt-3 flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 py-2 transition-colors"
                  >
                    <RotateCcw size={14} />
                    Start Over
                  </button>
                )}
          </>
        )}
      </div>
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
    </ProGate>
  );
}

// ==================== Sub-components ====================

function OptionGroup({ label, options, value, onChange, disabled }) {
  return (
    <div className={disabled ? 'opacity-40 pointer-events-none' : ''}>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
        {label}
      </label>
      <div className="flex flex-col gap-1">
        {options.map((opt) => (
          <button
            key={opt.id}
            onClick={() => onChange(opt.id)}
            className={`px-3 py-1.5 text-sm rounded-lg text-left transition-colors ${
              value === opt.id
                ? 'bg-brand-blue text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ==================== Helpers ====================

function readFileAsDataUrl(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

function getImageDimensions(dataUrl) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = dataUrl;
  });
}

async function normalizeImage(dataUrl) {
  const isJpeg = dataUrl.includes('image/jpeg') || dataUrl.includes('image/jpg');
  if (isJpeg || dataUrl.includes('image/png')) return dataUrl;

  const img = new window.Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = dataUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  canvas.getContext('2d').drawImage(img, 0, 0);
  return canvas.toDataURL('image/png');
}
